/**
 * Hoffmaster Menu Widget Handler
 * Handles desktop dropdown navigation, mobile menu toggle, safe zone tracking,
 * and mouse position tracking for gap detection.
 */

import './_menu.scss';

class HoffmasterMenuHandler extends elementorModules.frontend.handlers.Base {
	getDefaultSettings() {
		return {
			selectors: {
				nav: '.hm-header__nav',
				item: '.hm-header__nav__item',
				itemHasChildren: '.hm-header__nav__item--has-children',
				submenuContainer: '.hm-header__nav__submenu--container',
				submenuUl: '.hm-header__nav__submenu',
				mobileToggle: '.hm-header__mobile-toggle',
				menuWrapper: '.hm-header__menu-wrapper',
				navLink: '.hm-header__nav__link',
				dropdownToggle: '.hm-header__nav__dropdown-toggle',
			},
			constants: {
				closeDelay: 150,
				activeClass: 'is-open',
				safeZoneAttr: 'data-hm-safe-zone',
				safeZonePadding: 32,
				mobileBreakpoint: 1024,
			},
		};
	}

	getDefaultElements() {
		const selectors = this.getSettings('selectors');
		return {
			$nav: this.$element.find(selectors.nav),
			$itemsHasChildren: this.$element.find(selectors.itemHasChildren),
			$allItems: this.$element.find(selectors.item),
			$mobileToggle: this.$element.find(selectors.mobileToggle),
			$menuWrapper: this.$element.find(selectors.menuWrapper),
		};
	}

	bindEvents() {
		// Add JS flag
		document.documentElement.classList.remove('no-js');
		document.documentElement.classList.add('js');

		// Initialize instance state
		this.closeTimers = new WeakMap();
		this.safeZones = new WeakMap();
		this.mouse = { x: 0, y: 0 };

		// Track global mouse position for safe zone checks
		this.mouseMoveHandler = (e) => {
			this.mouse = { x: e.clientX, y: e.clientY };
		};
		document.addEventListener('mousemove', this.mouseMoveHandler);

		// Prevent default click on items without URLs
		this.initNoLinkPrevention();

		// Initialize desktop navigation
		if (this.elements.$nav.length) {
			this.initDesktopNav();
		}

		// Initialize mobile menu
		if (this.elements.$mobileToggle.length && this.elements.$menuWrapper.length) {
			this.initMobileMenu();
		}

		// Initialize mobile dropdown toggles once
		this.initMobileDropdownToggles();
	}

	initDesktopNav() {
		const { mobileBreakpoint } = this.getSettings('constants');

		this.elements.$itemsHasChildren.each((index, item) => {
			const $item = jQuery(item);

			$item.on('mouseenter', () => {
				// Only apply hover behavior on desktop
				if (window.innerWidth <= mobileBreakpoint) return;
				item.classList.add(this.getSettings('constants').activeClass);
			});

			$item.on('mouseleave', () => {
				// Only apply hover behavior on desktop
				if (window.innerWidth <= mobileBreakpoint) return;
				item.classList.remove(this.getSettings('constants').activeClass);
			});

			// Add event click to item to not loose its linking property
			const $link = $item.find(`:scope > ${this.getSettings('selectors').navLink}`);
			$link.on('click', (e) => {
				// Only handle click on desktop (on mobile, the toggle button handles this)
				if (window.innerWidth <= mobileBreakpoint) return;
				e.preventDefault();
				window.location.href = $link.attr('href');
			});
		});
	}

	initNoLinkPrevention() {
		// Prevent default click behavior on menu items without URLs
		// Use '>' to only select direct child links, not nested descendant links
		const $noLinkItems = this.$element.find('.hm-header__nav__item--no-link > .hm-header__nav__link');

		$noLinkItems.on('click', (e) => {
			e.preventDefault();
		});
	}

	bindItem(item) {
		const selectors = this.getSettings('selectors');
		const $item = jQuery(item);
		const submenu = item.querySelector(`:scope > ${selectors.submenuContainer}`);

		if (!submenu) return;

		const $submenu = jQuery(submenu);

		$item.on('mouseenter', () => {
			this.openItem(item);
		});

		$item.on('mouseleave', (e) => {
			this.handleItemMouseLeave(item, submenu, e);
		});

		$submenu.on('mouseenter', () => {
			this.cancelClose(item);
			this.removeSafeZone(item);
		});

		$submenu.on('mouseleave', (e) => {
			const rt = e.relatedTarget;
			if (rt && submenu.contains(rt)) return;
			if (rt && item.contains(rt)) return;
			this.closeItem(item);
		});
	}

	handleItemMouseLeave(item, submenu, e) {
		const selectors = this.getSettings('selectors');
		const rt = e.relatedTarget;

		// CASE 1: Cursor moved into own submenu
		if (rt && submenu.contains(rt)) {
			return;
		}

		// CASE 2: Cursor still in parent list
		const parentList = item.parentElement;
		if (rt && parentList && (parentList === rt || parentList.contains(rt))) {
			return;
		}

		// CASE 3: Cursor left branch, schedule close with safe zone
		const itemRect = item.getBoundingClientRect();
		const innerUl = submenu.querySelector(selectors.submenuUl);
		const panelRect = (innerUl || submenu).getBoundingClientRect();
		const cursorX = e.clientX;
		const cursorY = e.clientY;

		this.scheduleClose(item, submenu, itemRect, panelRect, cursorX, cursorY);
	}

	handleNavLeave() {
		const { safeZoneAttr } = this.getSettings('constants');
		const { itemHasChildren, activeClass } = this.getSettings('selectors');

		if (document.querySelector(`[${safeZoneAttr}]`)) {
			return;
		}

		this.elements.$nav
			.find(`.${itemHasChildren.replace('.', '')}.${activeClass.replace('.', '')}`)
			.each((index, item) => {
				this.closeItem(item, true);
			});
	}

	closeSiblings(item) {
		const selectors = this.getSettings('selectors');
		const parent = item.parentElement;

		if (!parent) return;

		jQuery(parent)
			.find(`:scope > ${selectors.itemHasChildren}`)
			.each((index, sibling) => {
				if (sibling !== item) {
					this.closeItem(sibling, true);
				}
			});
	}

	openItem(item) {
		const { activeClass } = this.getSettings('constants');

		this.cancelClose(item);
		this.removeSafeZone(item);
		this.closeSiblings(item);
		item.classList.add(activeClass);
	}

	closeItem(item, immediate = false) {
		const { activeClass, closeDelay } = this.getSettings('constants');
		const selectors = this.getSettings('selectors');

		const doClose = () => {
			item.classList.remove(activeClass);
			this.removeSafeZone(item);
			jQuery(item)
				.find(`${selectors.itemHasChildren}.${activeClass}`)
				.each((index, child) => {
					child.classList.remove(activeClass);
					this.removeSafeZone(child);
				});
		};

		if (immediate) {
			this.cancelClose(item);
			doClose();
			return;
		}

		const timer = setTimeout(doClose, closeDelay);
		this.closeTimers.set(item, timer);
	}

	cancelClose(item) {
		const timer = this.closeTimers.get(item);
		if (timer) {
			clearTimeout(timer);
			this.closeTimers.delete(item);
		}
	}

	scheduleClose(item, submenu, itemRect, panelRect, cursorX, cursorY) {
		const { safeZoneAttr, safeZonePadding, closeDelay } = this.getSettings('constants');

		this.cancelClose(item);
		this.removeAllSafeZones();

		if (panelRect.width === 0 || panelRect.height === 0) {
			this.closeItem(item);
			return;
		}

		const pad = safeZonePadding;
		let top, left, width, height;

		// Panel is to the RIGHT
		if (panelRect.left > itemRect.right - 5) {
			top = Math.min(itemRect.top, panelRect.top) - pad;
			left = itemRect.right;
			width = panelRect.left - itemRect.right + pad;
			height = Math.max(itemRect.bottom, panelRect.bottom) - top + pad;
		}
		// Panel is BELOW
		else if (panelRect.top > itemRect.bottom - 5) {
			top = itemRect.bottom;
			left = panelRect.left - pad;
			width = panelRect.width + pad * 2;
			height = panelRect.top - itemRect.bottom + pad;
		} else {
			this.closeItem(item);
			return;
		}

		const zone = document.createElement('div');
		zone.setAttribute(safeZoneAttr, '');
		zone.setAttribute('aria-hidden', 'true');
		zone.style.cssText = `
			position: fixed;
			top: ${top}px;
			left: ${left}px;
			width: ${width}px;
			height: ${height}px;
			z-index: 9998;
			pointer-events: all;
			background-color: red;
			opacity: 0.5;
		`;

		zone.addEventListener('mouseenter', () => {
			this.cancelClose(item);
		});

		zone.addEventListener('mouseleave', (e) => {
			const rt = e.relatedTarget;

			if (rt && rt.hasAttribute && rt.hasAttribute(safeZoneAttr)) {
				return;
			}
			if (rt && submenu.contains(rt)) {
				this.cancelClose(item);
				this.removeSafeZone(item);
				return;
			}
			if (rt && item.contains(rt)) {
				this.cancelClose(item);
				this.removeSafeZone(item);
				return;
			}

			this.closeItem(item);
			this.removeSafeZone(item);
		});

		document.body.appendChild(zone);
		this.safeZones.set(item, zone);

		// Check if cursor is already inside the zone
		const zoneRect = zone.getBoundingClientRect();
		const cursorAlreadyInside =
			cursorX >= zoneRect.left &&
			cursorX <= zoneRect.right &&
			cursorY >= zoneRect.top &&
			cursorY <= zoneRect.bottom;

		if (cursorAlreadyInside) {
			this.cancelClose(item);
		} else {
			const timer = setTimeout(() => {
				if (!item.matches(':hover') && !submenu.matches(':hover')) {
					this.closeItem(item, true);
				}
			}, closeDelay);
			this.closeTimers.set(item, timer);
		}
	}

	removeSafeZone(item) {
		const zone = this.safeZones.get(item);
		if (zone) {
			zone.remove();
			this.safeZones.delete(item);
		}
	}

	removeAllSafeZones() {
		const { safeZoneAttr } = this.getSettings('constants');
		const selectors = this.getSettings('selectors');

		document.querySelectorAll(`[${safeZoneAttr}]`).forEach((z) => z.remove());
		document.querySelectorAll(selectors.itemHasChildren).forEach((item) => {
			if (this.safeZones.has(item)) {
				this.safeZones.delete(item);
			}
		});
	}

	initMobileMenu() {
		const { activeClass } = this.getSettings('constants');

		this.elements.$mobileToggle.on('click', () => {
			const isOpen = this.elements.$mobileToggle.attr('aria-expanded') === 'true';

			this.elements.$mobileToggle.attr('aria-expanded', !isOpen);
			this.elements.$menuWrapper.toggleClass(activeClass);

			document.body.style.overflow = !isOpen ? 'hidden' : '';
		});

		// Close menu when clicking outside
		this.outsideClickHandler = (e) => {
			const isOpen = this.elements.$menuWrapper.hasClass(activeClass);
			if (!isOpen) return;

			const isClickInside =
				this.elements.$menuWrapper[0].contains(e.target) ||
				this.elements.$mobileToggle[0].contains(e.target);

			if (!isClickInside) {
				this.elements.$mobileToggle.attr('aria-expanded', 'false');
				this.elements.$menuWrapper.removeClass(activeClass);
				document.body.style.overflow = '';
			}
		};
		document.addEventListener('click', this.outsideClickHandler);

		// Re-init on resize
		this.resizeHandler = () => {
			const { mobileBreakpoint } = this.getSettings('constants');
			clearTimeout(this.resizeTimer);
			this.resizeTimer = setTimeout(() => {
				if (window.innerWidth > mobileBreakpoint) {
					this.elements.$mobileToggle.attr('aria-expanded', 'false');
					this.elements.$menuWrapper.removeClass(activeClass);
					document.body.style.overflow = '';
				}
			}, 250);
		};
		jQuery(window).on('resize', this.resizeHandler);
	}

	initMobileDropdownToggles() {
		const { activeClass, mobileBreakpoint } = this.getSettings('constants');
		const selectors = this.getSettings('selectors');

		// Find ALL dropdown toggles (not just top level)
		const $allToggles = this.$element.find(selectors.dropdownToggle);

		$allToggles.each((index, toggle) => {
			const $toggle = jQuery(toggle);
			// Find the immediate parent item (direct parent <li>)
			const $item = $toggle.closest(selectors.itemHasChildren);

			$toggle.on('click', (e) => {
				e.preventDefault();
				e.stopPropagation();

				// Only work on mobile
				if (window.innerWidth > mobileBreakpoint) return;

				const isOpen = $item.hasClass(activeClass);
				$item.toggleClass(activeClass);
				$toggle.attr('aria-expanded', !isOpen);
			});
		});
	}

	cleanup() {
		// Remove global event listeners
		if (this.mouseMoveHandler) {
			document.removeEventListener('mousemove', this.mouseMoveHandler);
		}

		if (this.outsideClickHandler) {
			document.removeEventListener('click', this.outsideClickHandler);
		}

		if (this.resizeHandler) {
			jQuery(window).off('resize', this.resizeHandler);
		}

		// Clear all timers
		this.elements.$itemsHasChildren.each((index, item) => {
			this.cancelClose(item);
		});

		// Remove all safe zones
		this.removeAllSafeZones();
	}

	onDestroy() {
		this.cleanup();
	}
}

jQuery(window).on('elementor/frontend/init', () => {
	elementorFrontend.elementsHandler.attachHandler('hoffmaster-menu', HoffmasterMenuHandler);
});
